import React, { useEffect, useState } from 'react';
import { listUsers, createUser, setUserRole, setUserEnabled, TenantUser, TenantRole } from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';

// ---------------------------------------------------------------------------
// Users tab (customer-admin, tenant-scoped — gate G3).
//
// Lists, creates, re-roles, and enables/disables the users of the SIGNED-IN
// customer only. The tenant is NEVER chosen here — the backend force-stamps it
// from the caller's token, so an admin structurally cannot touch another customer's
// users. Role is limited to Admin / User (the operator SuperAdmin role is never
// offered). This page is a UX layer; the backend is the security boundary.
// ---------------------------------------------------------------------------

const ROLES: TenantRole[] = ['Admin', 'User'];

const UsersView: React.FC = () => {
  const { customerId } = useAuth();
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState<{ text: string; kind: 'info' | 'error' } | null>(null);
  const [busy, setBusy] = useState<string | null>(null); // username currently mutating

  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState<TenantRole>('User');
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await listUsers();
      setUsers(res.users || []);
    } catch (e) {
      console.error(e);
      setNote({ text: 'Failed to load users.', kind: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submitCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = newEmail.trim();
    if (!email) { setNote({ text: 'Enter an email address.', kind: 'error' }); return; }
    setCreating(true);
    setNote(null);
    try {
      await createUser(email, newRole);
      setNewEmail('');
      setNewRole('User');
      // Copy must match what the pool actually does. With mfa_configuration = "ON"
      // the invite is not "set a password" — it is temporary password, then a
      // permanent one, then MANDATORY authenticator enrolment with no skip. An
      // admin who does not warn the new user to have their phone ready hands them
      // a half-finished sign-in.
      setNote({
        text: `Invited ${email} as ${newRole}. They'll get an email with a temporary password; at first sign-in they set a permanent one and enrol an authenticator app, so they'll need their phone to hand.`,
        kind: 'info',
      });
      await load();
    } catch (err: any) {
      console.error(err);
      setNote({ text: err?.response?.data?.error || 'Could not create that user.', kind: 'error' });
    } finally {
      setCreating(false);
    }
  };

  const changeRole = async (u: TenantUser, role: TenantRole) => {
    if (role === u.role || busy) return;
    setBusy(u.username); setNote(null);
    try {
      await setUserRole(u.username, role);
      setUsers(list => list.map(x => x.username === u.username ? { ...x, role } : x));
      setNote({ text: `${u.email || u.username} is now ${role}.`, kind: 'info' });
    } catch (err: any) {
      console.error(err);
      setNote({ text: err?.response?.data?.error || 'Could not change that role.', kind: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const toggleEnabled = async (u: TenantUser) => {
    if (busy) return;
    const next = !(u.enabled ?? true);
    setBusy(u.username); setNote(null);
    try {
      await setUserEnabled(u.username, next);
      setUsers(list => list.map(x => x.username === u.username ? { ...x, enabled: next } : x));
      setNote({ text: `${u.email || u.username} ${next ? 'enabled' : 'disabled'}.`, kind: 'info' });
    } catch (err: any) {
      console.error(err);
      setNote({ text: err?.response?.data?.error || 'Could not update that user.', kind: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const cell: React.CSSProperties = { padding: '8px 10px', borderBottom: '1px solid var(--border-subtle)', textAlign: 'left' };

  return (
    <div style={{ padding: '8px 4px 24px', maxWidth: 860 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', marginBottom: '6px' }}>
        <h1 style={{ margin: 0, fontSize: '26px' }}>Users</h1>
        {customerId && <span style={{ fontSize: '0.78em', color: 'var(--text-muted)' }}>Customer: {customerId}</span>}
      </div>
      <div style={{ fontSize: '0.8em', color: 'var(--text-muted)', marginBottom: '14px' }}>
        Everyone in your organization. New users get an emailed temporary password, then set a permanent one and
        {' '}enrol an authenticator app at first sign-in — that step is required, so they'll need a phone to hand.
        {' '}Only <strong>your</strong> users are shown and manageable — the tenant is enforced by the server.
      </div>

      {note && (
        <div style={{ marginBottom: '12px', fontSize: '0.85em', padding: '6px 10px', borderRadius: '6px', background: note.kind === 'error' ? 'rgba(220,38,38,0.10)' : 'rgba(31,157,85,0.10)', color: note.kind === 'error' ? 'var(--danger-text, #dc2626)' : 'var(--success-text, #157347)', border: '1px solid ' + (note.kind === 'error' ? 'rgba(220,38,38,0.3)' : 'rgba(31,157,85,0.3)') }}>
          {note.text}
        </div>
      )}

      <form onSubmit={submitCreate} style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', padding: '10px 12px', border: '1px dashed var(--border-soft)', borderRadius: '8px', background: 'var(--bg-surface-raised)', marginBottom: '16px' }}>
        <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="new.user@example.com" className="form-control" style={{ maxWidth: 260 }} />
        <select value={newRole} onChange={e => setNewRole(e.target.value as TenantRole)} className="form-control" style={{ maxWidth: 140 }}>
          {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <button type="submit" disabled={creating} style={{ padding: '7px 16px', borderRadius: '6px', border: '1px solid var(--or-blue-mid, #2563eb)', background: 'var(--or-blue-mid, #2563eb)', color: '#fff', fontWeight: 600, cursor: creating ? 'default' : 'pointer', opacity: creating ? 0.6 : 1 }}>
          {creating ? 'Inviting…' : 'Invite user'}
        </button>
      </form>

      {loading ? (
        <div style={{ color: 'var(--text-muted)' }}>Loading…</div>
      ) : (
        <div style={{ border: '1px solid var(--border-soft)', borderRadius: '8px', overflow: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.88em' }}>
            <thead>
              <tr style={{ background: 'var(--bg-surface-raised)' }}>
                <th style={{ ...cell, fontWeight: 700 }}>Email</th>
                <th style={{ ...cell, fontWeight: 700 }}>Status</th>
                <th style={{ ...cell, fontWeight: 700 }}>Role</th>
                <th style={{ ...cell, fontWeight: 700 }}>Access</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => {
                const enabled = u.enabled ?? true;
                const isBusy = busy === u.username;
                return (
                  <tr key={u.username} style={{ opacity: enabled ? 1 : 0.55 }}>
                    <td style={cell}>{u.email || u.username}</td>
                    <td style={{ ...cell, color: 'var(--text-muted)' }}>{u.status || (enabled ? 'ACTIVE' : 'DISABLED')}</td>
                    <td style={cell}>
                      <select value={u.role} disabled={isBusy} onChange={e => changeRole(u, e.target.value as TenantRole)} className="form-control" style={{ maxWidth: 120 }}>
                        {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </td>
                    <td style={cell}>
                      <button type="button" disabled={isBusy} onClick={() => toggleEnabled(u)} style={{ padding: '4px 12px', borderRadius: '6px', border: '1px solid var(--border-soft)', background: 'var(--bg-surface)', color: enabled ? 'var(--danger-text, #dc2626)' : 'var(--success-text, #157347)', fontWeight: 600, cursor: isBusy ? 'default' : 'pointer' }}>
                        {enabled ? 'Disable' : 'Enable'}
                      </button>
                    </td>
                  </tr>
                );
              })}
              {users.length === 0 && (
                <tr><td colSpan={4} style={{ ...cell, color: 'var(--text-faint)' }}>No users yet — invite one above.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UsersView;
